<template>
  <label class="ux-field-title" v-if="showLabel"
    >{{ label }}<span class="ux-required-marker" v-if="required">*</span></label
  >

  <div class="ux-field-description" v-if="showDescription">
    {{ description }}
  </div>
  <div v-if="multiValue">
    <flex-row class="ux-text-row" :key="index" v-for="(entry, index) in model">
      <flex-cell>
        <date-object :allDay="allDay" v-model="model[index]" />
      </flex-cell>
      <flex-cell shrink vcenter>
        <ux-button tag="a" icon v-if="canRemoveValue" @click="remove(entry)">
          <ux-icon icon="fa-times" />
        </ux-button>
      </flex-cell>
    </flex-row>
    <ux-button size="sm" v-if="canAddValue" @click="add()">{{
      addLabel
    }}</ux-button>
  </div>
  <template v-else>
    <date-object :allDay="allDay" v-model="model" />
  </template>
</template>
<script>
import InputMixin from "./input-mixin";
import DateObject from "./dateobject.vue";
import { DateTime } from "luxon";

export default {
  mixins: [InputMixin],
  components: {
    DateObject,
  },
  props: {
    modelValue: {
      type: Object,
    },
  },
  computed: {
    allDay() {
      return this.field.allDay || this.field.dateOnly;
    },

    // Override the model computed to handle proper data flow
    model: {
      get() {
        // For the dateobject widget, always return dateobject format
        // cleanInput handles conversion from dates/strings to dateobject format
        return this.cleanInput(this.value);
      },
      set(val) {
        let force;

        if (this.isGroup || this.widget === "form") {
          // Check if this is a proxy and if it's been updated
          if (val === this.value) {
            force = false;
          } else {
            force = true;
          }
        }

        const cleanedValue = this.cleanInput(val);
        const cleanedModel = this.cleanInput(this.value);
        const contentHasChanged =
          JSON.stringify(cleanedValue) != JSON.stringify(cleanedModel);

        // Check if there has actually been a change
        if (contentHasChanged || force) {
          this.value = cleanedValue;
          this.checkAutofocus();
          // Apply output transformation when dispatching
          const outputValue = this.cleanOutputValue(cleanedValue);
          this.$emit("update:modelValue", outputValue);
        }
      },
    },
  },
  methods: {
    getNewDefaultEntry() {
      return {};
    },

    cleanOutputValue(val) {
      // If field type is 'date' and we have a dateobject format, convert to JavaScript Date
      if (
        this.field.type === "date" &&
        val &&
        typeof val === "object" &&
        val.year !== undefined
      ) {
        try {
          // Handle 12-hour to 24-hour conversion
          let hour = val.hour || 0;
          if (val.meridian === "PM" && hour !== 12) {
            hour = hour + 12;
          } else if (val.meridian === "AM" && hour === 12) {
            hour = 0;
          }

          // Create DateTime object using Luxon
          const dateTime = DateTime.fromObject({
            year: val.year,
            month: val.month,
            day: val.day,
            hour: hour,
            minute: val.minute || 0,
            second: val.second || 0,
            millisecond: val.millisecond || 0,
          });

          // Return JavaScript Date if valid, otherwise return original value
          if (dateTime.isValid) {
            return dateTime.toJSDate();
          } else {
            console.warn("Invalid date object:", val, dateTime.invalidReason);
            return val;
          }
        } catch (error) {
          console.warn("Error converting dateobject to Date:", error, val);
          return val;
        }
      }

      // For non-date types or invalid objects, return as-is
      return val;
    },

    cleanInputValue(val) {
      // If field type is 'date', handle both Date objects and date strings
      if (this.field.type === "date") {
        let dateTime;

        // Handle JavaScript Date objects
        if (val instanceof Date && !isNaN(val.getTime())) {
          try {
            dateTime = DateTime.fromJSDate(val);
          } catch (error) {
            console.warn("Error parsing JavaScript Date:", error, val);
            return val;
          }
        }
        // Handle date strings (ISO format, etc.)
        else if (typeof val === "string" && val.trim().length > 0) {
          try {
            dateTime = DateTime.fromISO(val);
            // If ISO parsing fails, try other common formats
            if (!dateTime.isValid) {
              dateTime = DateTime.fromJSDate(new Date(val));
            }
          } catch (error) {
            console.warn("Error parsing date string:", error, val);
            return val;
          }
        }
        // If we have an existing dateobject format, pass it through
        else if (val && typeof val === "object" && val.year !== undefined) {
          return val;
        }
        // For other values, return as-is
        else {
          return val;
        }

        // Convert valid DateTime to dateobject format
        if (dateTime && dateTime.isValid) {
          try {
            // Convert 24-hour to 12-hour format
            let hour = dateTime.hour;
            let meridian = "AM";

            if (hour >= 12) {
              meridian = "PM";
              if (hour > 12) {
                hour = hour - 12;
              }
            } else if (hour === 0) {
              hour = 12;
            }

            return {
              millisecond: dateTime.millisecond,
              second: dateTime.second,
              minute: dateTime.minute,
              hour: hour,
              day: dateTime.day,
              month: dateTime.month,
              year: dateTime.year,
              meridian: meridian,
            };
          } catch (error) {
            console.warn(
              "Error converting DateTime to dateobject:",
              error,
              val,
            );
            return val;
          }
        } else {
          console.warn("Invalid date value:", val, dateTime?.invalidReason);
          return val;
        }
      }

      // For non-date types, return as-is
      return val;
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-text-row {
  margin-bottom: 0.5em;
}

.ux-textarea,
textarea {
  border-radius: 0.1em;
  padding: 0.5em;
  box-sizing: border-box;
  background: rgba(#000, 0.05);
  border: 1px solid rgba(#000, 0.1);
  font-size: inherit;
  appearance: none;
  line-height: 1;
  min-height: 5em;
  font-family: inherit;
  resize: vertical;
  overflow: hidden;

  &:focus {
    background: none;
    border: 1px solid var(--primary);
    outline: none;
  }
}

.ux-text-row .ux-btn {
  margin-left: 0.25em;
}

.ux-text-area-multiple {
  width: 100%;
}

.ux-text-area-single {
  width: 100%;
  // width: calc(100% - 0.5em);
  // margin: 0 0.5em 0 0;
}
</style>
